import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FilterGroup } from '@robocorp/components';
import { WorkItemSearchArgs } from '~/queries';
import { useDisclosure } from '~/hooks';
import { ExtractProps } from '~/types';
import { BulkConfirmationDialog } from './dialog/BulkConfirmation';

interface Props extends Omit<ExtractProps<typeof Button>, 'onClick' | 'children'> {
  onConfirm: () => Promise<void>;
  activeFilters?: WorkItemSearchArgs;
  filterOptions?: Record<string, FilterGroup>;
  itemCount: number;
}

interface AbstractProps extends Props {
  translationPartialKey: string;
  confirmationButtonText?: string;
}

const BulkAction: FC<AbstractProps> = ({
  onConfirm,
  activeFilters,
  filterOptions,
  itemCount: count,
  translationPartialKey,
  confirmationButtonText,
  loading,
  ...restProps
}) => {
  const { t } = useTranslation();

  const [show, showHandler] = useDisclosure(false);
  const handleConfirm = () => onConfirm().then(showHandler.hide);
  const isBulkAction = count === Infinity;

  return (
    <>
      {count > 0 && (
        <Button onClick={showHandler.show} variant="ghost" loading={loading} {...restProps}>
          {t(`${translationPartialKey}.action`)}
        </Button>
      )}
      <BulkConfirmationDialog
        isOpen={show}
        isLoading={loading}
        onClose={showHandler.hide}
        onConfirm={handleConfirm}
        filters={isBulkAction ? activeFilters : undefined}
        filterOptions={isBulkAction ? filterOptions : undefined}
        title={t('confirmationDialog.title')}
        description={
          isBulkAction
            ? t(`${translationPartialKey}.description_all`)
            : t(`${translationPartialKey}.description`, { count })
        }
        confirmButtonText={confirmationButtonText ?? t('confirm')}
      />
    </>
  );
};

export const BulkStartRunAction: FC<Props> = (props) => (
  <BulkAction translationPartialKey="dataExplorer.confirmRun" {...props} />
);

export const BulkRetryAction: FC<Props> = (props) => (
  <BulkAction translationPartialKey="dataExplorer.confirmRetry" {...props} />
);

export const BulkDiscardAction: FC<Props> = (props) => (
  <BulkAction translationPartialKey="dataExplorer.confirmDiscard" {...props} />
);

export const BulkDeleteAction: FC<Props> = (props) => {
  const { t } = useTranslation();
  return (
    <BulkAction
      translationPartialKey="dataExplorer.confirmDelete"
      confirmationButtonText={t('delete')}
      {...props}
    />
  );
};
